"use client";
import React from "react";
import { IResource } from "@gettactic/api";
import { mergeRefs, useBooleanState } from "@gettactic/helpers/src";
import {
  UseSvgMapInteractionsReturn,
  useSvgMapInteractions
} from "@gettactic/helpers/src/map/svg/useSvgMapInteractions";
import { useMap } from "@/features/map/MapProvider";
import { useMapBuilderInteractiveZones } from "@/features/map/components/MapView/useMapBuilderInteractiveZones";
import {
  MapInteractiveZoneActionOptions,
  ZoneLink
} from "@gettactic/map-builder/src/types";
import { FloorMap } from "@/features/map/components/MapView/FloorItem/FloorMap";
import { MapViewSvgMiniMap } from "@/features/map/components/MapView/MapViewSvg/MapViewSvgMiniMap";
import { useInitialMapTransform } from "@/features/map/components/MapView/useInitialMapTransform";
import { useReserve } from "@/features/reserve/providers/context";

export interface MapViewSvgFloorItemProps {
  floor: IResource;
  floorContainerRef: React.RefObject<HTMLDivElement | null>;
  mapContainerRef: React.RefObject<HTMLDivElement | null>;
  index: number;
  isVisible?: boolean;
  mapRef?: React.RefObject<HTMLDivElement | null>;
  onMapReady:
    | ((
        floor: IResource | null,
        svg: SVGSVGElement,
        mapInteractions: UseSvgMapInteractionsReturn
      ) => void)
    | undefined;
  onMapReadyEditing:
    | ((
        floor: IResource | null,
        svg: SVGSVGElement,
        mapInteractions: UseSvgMapInteractionsReturn
      ) => void)
    | undefined;
  isMapEditing?: boolean;
  showMiniMap?: boolean;
  allowInteractionsOnFocussedResource?: boolean;
  embedded?: boolean;
  /** Map coordinates to centre/zoom on once the SVG is ready (e.g. a pin). */
  focusPoint?: { x: number; y: number };
  /** Overrides the embedded target zoom used when focusing. */
  focusZoom?: number;
}

export const MapViewSvgFloorItem: React.FC<
  React.PropsWithChildren<MapViewSvgFloorItemProps>
> = ({
  floor,
  index,
  children,
  isVisible,
  mapRef,
  floorContainerRef,
  mapContainerRef,
  onMapReady,
  onMapReadyEditing,
  isMapEditing,
  showMiniMap = true,
  allowInteractionsOnFocussedResource = false,
  embedded = false,
  focusPoint,
  focusZoom
}) => {
  const {
    events,
    allResources,
    schedulable,
    locateResourceId,
    setLocateResourceId,
    onFloorReady,
    isOnReservationModal,
    isReservationCardIsOpen,
    navigateToOfficeFloor
  } = useMap();

  // Native interactive zones for map-builder maps (undefined for legacy
  // maps, which keep the clone-overlay path).
  const interactiveZoneInfo = useMapBuilderInteractiveZones();

  const handleHighlightSpot = React.useCallback(
    (resId: string | null, _options: MapInteractiveZoneActionOptions) => {
      // Same event the overlay used to fire: it feeds the hover-details
      // card (MapViewOverlays / MapViewHoverDetails) and the pulse layer.
      const res = resId
        ? schedulable?.find((r) => r.id === resId) || null
        : null;
      events.trigger("onHighlightResource", res);
    },
    [events, schedulable]
  );

  const handleZoneLinkClick = React.useCallback(
    (link: ZoneLink) => {
      if (link.type === "url" && link.url) {
        // Only allow web URLs — map_data is org-editable content, so a
        // javascript: URL must never reach window.open.
        try {
          const parsed = new URL(link.url);
          if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
            return;
          }
        } catch {
          return;
        }
        window.open(link.url, "_blank", "noopener,noreferrer");
        return;
      }
      if (link.type === "office" && link.officeId) {
        navigateToOfficeFloor(link.officeId, link.floorId);
      }
    },
    [navigateToOfficeFloor]
  );

  const { isReserveOpen } = useReserve();

  const self = React.useRef<HTMLDivElement | null>(null);
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const mapOriginalBox = React.useRef<DOMRect | null>(null);
  const isSvgLoaded = useBooleanState();
  const isMapBuilderCentered = useBooleanState();
  const needsMapBuilderCenter = React.useRef(false);
  const [loadingIterator, setLoadingIterator] = React.useState(0);

  const svgMapInteractions = useSvgMapInteractions(
    svgRef,
    self,
    floorContainerRef,
    {
      allowInteractionsWhenFocussed:
        allowInteractionsOnFocussedResource || isReservationCardIsOpen,
      enabled: isSvgLoaded.state,
      desiredZoom: embedded ? (focusZoom ?? 0.75) : 1.5,
      // When an embedded preview passes focusZoom, frame the target at exactly
      // that scale (zooming in or out) rather than only zooming in past the fit.
      exactZoom: embedded ? focusZoom : undefined,
      tiltMax: 0 // Set to 15 to re-enable 3D tilt effect
    }
  );

  const mapTransform = useInitialMapTransform({
    itemWrapperRef: self,
    isSvgLoaded: isSvgLoaded.state,
    floorContainerRef,
    mapContainerRef
  });

  // The mini map has to escape the panned/zoomed map layers, but portaling it
  // to `document.body` also lifts it out of the app's `isolate` stacking
  // context — where it covered dialogs rendered *contained* inside the map
  // (e.g. the workplace-request wizard, whose footer buttons became
  // unclickable). Anchor it to the map container instead: still outside the
  // transformed layers, but back under the map's own dialog overlays.
  const mapChromeContainer =
    (mapContainerRef.current?.closest(
      ".full-map-container"
    ) as HTMLElement | null) ?? null;

  React.useEffect(() => {
    if (isSvgLoaded.state && self.current) {
      const svgElement = self.current.querySelector(".injected-svg") as
        | SVGSVGElement
        | undefined;
      //if (svgElement) onMapReady?.(floor, svgElement, svgMapInteractions);
    }
  }, [isSvgLoaded.state, self.current]);

  // Center map-builder maps after the render has committed, so the DOM
  // change from centerAndFitMap isn't overwritten by React's inline style.
  React.useEffect(() => {
    if (isSvgLoaded.state && needsMapBuilderCenter.current) {
      needsMapBuilderCenter.current = false;
      svgMapInteractions.centerAndFitMap({ animate: false });
      isMapBuilderCentered.true();
    }
  }, [isSvgLoaded.state]);

  React.useEffect(() => {
    if (
      isSvgLoaded.state &&
      locateResourceId &&
      svgMapInteractions.updateCount
    ) {
      const resource = (allResources || []).find(
        (res) => res.id === locateResourceId
      );
      if (!resource) {
        console.warn(`Resource with ID ${locateResourceId} not found.`);
        return;
      }

      // Only handle locate on the floor that actually owns this resource.
      // This avoids races where the previous floor consumes the locate signal
      // before the new floor has mounted.
      if (resource.parent_id && resource.parent_id !== floor.id) {
        return;
      }

      // Skip zoom when in reservation modal or when actively reserving — but
      // not for embedded previews, which have their own map and must always
      // frame their resource regardless of the page's reserve state.
      if (
        !embedded &&
        (isOnReservationModal || isReserveOpen || isReservationCardIsOpen)
      ) {
        // Still trigger locate event without zooming
        events.trigger("onLocateResource", resource);
        setLocateResourceId(null);
        return;
      }

      setTimeout(() => {
        svgMapInteractions.zoomToResource(resource?.id, {
          onComplete: () => {
            // highlight and locate the resource
            // events.trigger("onHighlightResource", resource);

            // locate the resource with schedules modal
            events.trigger("onLocateResource", resource);
          }
        });
      }, 500);
      setLocateResourceId(null); // Reset after zooming
    }
  }, [
    locateResourceId,
    isSvgLoaded.state,
    svgMapInteractions.updateCount,
    events,
    allResources,
    setLocateResourceId,
    isOnReservationModal,
    isReserveOpen,
    isReservationCardIsOpen
  ]);

  // Centre/zoom on a bare map coordinate (a pin) once the SVG is ready, mirroring
  // the resource focus path (which also fires on load, not on updateCount — that
  // stays 0 for embedded maps with no controls). Runs once per mount; the
  // embedded map re-mounts (via its MapProvider key) when the point changes. No
  // clear-timeout cleanup, so a re-render (focusPoint/svgMapInteractions change
  // identity) can't cancel the pending focus.
  const hasFocusedPointRef = React.useRef(false);
  React.useEffect(() => {
    if (hasFocusedPointRef.current || !isSvgLoaded.state || !focusPoint) {
      return;
    }
    hasFocusedPointRef.current = true;
    setTimeout(() => {
      svgMapInteractions.focusPoint(focusPoint.x, focusPoint.y);
    }, 500);
  }, [isSvgLoaded.state, focusPoint, svgMapInteractions]);

  React.useEffect(() => {
    const onFocusResource = (
      resource: IResource | null,
      options?: { noZoom?: boolean }
    ) => {
      // Skip all focus/zoom behavior when:
      // 1. In reservation modal (isOnReservationModal)
      // 2. OR when actively making a reservation on the map page (isReserveOpen)
      // 3. OR when reservation card is open (isReservationCardIsOpen)
      // 4. OR when explicitly requested via options
      // Embedded previews ignore 1–3: they render their own map and must always
      // frame their resource, even while the page is in a reserve flow.
      if (
        (!embedded &&
          (isOnReservationModal ||
            isReserveOpen ||
            isReservationCardIsOpen)) ||
        options?.noZoom
      ) {
        return;
      }

      if (!resource) {
        svgMapInteractions.blurResource();
        return;
      }

      svgMapInteractions.focusResource(resource?.id);
    };

    events.register("onFocusResource", onFocusResource);

    return () => {
      events.unregister("onFocusResource", onFocusResource);
    };
  }, [
    events,
    svgMapInteractions,
    isOnReservationModal,
    isReserveOpen,
    isReservationCardIsOpen
  ]);

  const svgStyle = {
    display: "block",
    borderRadius: 8,
    cursor: "default"
  };

  // Map-builder maps need content-aware centering (via centerAndFitMap)
  // which overrides the initial transform. Hide until centered to avoid a jump.
  const isMapBuilderMap = !!floor.map_data && !floor.map_url;
  const isWaitingForCenter = isMapBuilderMap && !isMapBuilderCentered.state;

  const itemStyle: React.HTMLAttributes<HTMLDivElement>["style"] = {
    transformOrigin: "top left",
    perspectiveOrigin: "center center",
    position: "absolute",
    left: 0,
    boxSizing: "border-box",
    transformStyle: "preserve-3d",
    transform: `rotateX(0deg) translate(${String(mapTransform?.translateX)}px, ${String(mapTransform?.translateY)}px) scale(${mapTransform?.scale}) translateZ(0px)`,
    //transition: "transform 0.4s ease, opacity 0.4s ease",
    pointerEvents: "auto",
    padding: 0,
    opacity: isWaitingForCenter ? 0 : 1
  };

  // Reset the state when the map is not visible
  React.useEffect(() => {
    if (!isVisible) {
      isSvgLoaded.false();
      isMapBuilderCentered.false();
      needsMapBuilderCenter.current = false;
    }
  }, [isVisible]);

  if (!isVisible) return null;
  return (
    <>
      <div
        className={"item-wrapper"}
        ref={mergeRefs([mapRef || null, self])}
        key={floor.id}
        style={itemStyle}
      >
        {/* MAP */}
        <FloorMap
          isEditing={isMapEditing}
          style={svgStyle}
          floor={floor}
          key={
            isMapEditing
              ? `url_${floor.id}_${index}`
              : `url_${floor.id}_${index}_editing`
          }
          onMapLoaded={(_, svg) => {
            if (!svg) return;
            svgRef.current = svg;
            isSvgLoaded.true();
            // Defer setMapReady so that SvgMiniMap's subscription effects
            // have a chance to register before the "ready" event fires.
            // This is needed for map-builder maps where the ref callback
            // fires synchronously during commit (before effects run).
            setTimeout(() => svgMapInteractions.setMapReady(), 0);
            onMapReady?.(floor, svg, svgMapInteractions);
            // Notify MapProvider that this floor's SVG is ready so it can
            // complete any pending locate/zoom flow without extra timers.
            onFloorReady(floor.id);
            // For map-builder maps, flag that content-aware centering is
            // needed. A useEffect handles the actual call so it runs after
            // React has committed the render (avoiding the inline style
            // from computeContainTransform overwriting the DOM change).
            if (svg.classList.contains("map-builder-svg")) {
              needsMapBuilderCenter.current = true;
            }
          }}
          onMapEditLoaded={(error, svg) => {
            if (!svg) return;
            svgRef.current = svg;
            isSvgLoaded.true();
            onMapReadyEditing?.(floor, svg, svgMapInteractions);
          }}
          resources={allResources}
          interactiveZoneInfo={interactiveZoneInfo}
          highlightResource={handleHighlightSpot}
          onZoneLinkClick={handleZoneLinkClick}
          label={floor.name ?? `Floor ${index + 1}`}
          showLabel
          embedded={embedded}
        />

        {children}
      </div>
      {showMiniMap && !isMapEditing && (
        <MapViewSvgMiniMap
          mapInteractor={svgMapInteractions}
          portalTarget={mapChromeContainer}
        />
      )}
    </>
  );
};
